import * as React from 'react';
import { liteClient } from 'algoliasearch/lite';
import classnames from 'classnames';
import { motion } from 'motion/react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { MediaQuery, Util, FocusTrap, LockBodyScroll, Backdrop } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';
import { FilteredIndex } from '../Algolia/components/FilteredIndex/FilteredIndex.js';
import { mapSearchIndicesToRecord } from '../Algolia/utils/map-search-indices-to-record.js';
import { SearchBox } from '../SearchBox/SearchBox.js';
import SearchResults from '../SearchResults/SearchResults.js';

const posedContent = {
    idle: {
        height: 0,
        overflow: 'hidden'
    },
    focusFixed: {
        height: 'auto',
        transition: {
            delay: 0.15,
            ease: 'easeIn'
        },
        transitionEnd: {
            overflow: 'auto'
        }
    },
    focusStatic: {
        height: 'auto',
        transition: {
            ease: 'easeInOut'
        },
        transitionEnd: {
            overflow: 'auto'
        }
    }
};
const posedSearchBar = {
    idle: {
        position: 'relative',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        transition: {
            duration: 0
        }
    },
    focusFixed: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 74,
        transition: {
            type: 'tween',
            ease: 'circOut',
            duration: 0.25
        }
    },
    focusStatic: {
        height: 74
    }
};
const SearchBar = ({ pageRoutes , onSubmit , onClose , onFocus , forceFocus , config , isMobile ,  })=>{
    const intl = useIntl();
    const currentBreakpoint = MediaQuery.useBreakpoint();
    const searchClient = liteClient(config.appId, config.apiKey);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [pose, setPose] = React.useState();
    const { productIndex , indexes =[]  } = config;
    let resultConfig = {};
    if (indexes && Array.isArray(indexes)) {
        resultConfig = mapSearchIndicesToRecord(indexes);
    }
    const isWideScreen = ()=>currentBreakpoint === MediaQuery.BreakpointName.TABLET_LANDSCAPE || currentBreakpoint === MediaQuery.BreakpointName.DESKTOP
    ;
    const handleFocus = React.useCallback(()=>{
        setHasFocus(true);
        onFocus && onFocus();
        Util.lockScroll();
    }, [
        onFocus
    ]);
    const handleClose = React.useCallback(()=>{
        setHasFocus(false);
        onClose && onClose();
        Util.unlockScroll();
    }, [
        onClose
    ]);
    React.useEffect(()=>{
        // Close search bar upon pressing esc if it has focus
        const closeSearchBar = (event)=>{
            if (event.key === 'Escape' && hasFocus) {
                handleClose();
            }
        };
        document.addEventListener('keydown', closeSearchBar, false);
        return ()=>{
            document.removeEventListener('keydown', closeSearchBar, false);
        };
    }, [
        hasFocus,
        handleClose
    ]);
    React.useEffect(()=>{
        const EMPTY = '';
        const IDLE = 'idle';
        const FOCUS_FIXED = 'focusFixed';
        const FOCUS_STATIC = 'focusStatic';
        setPose([
            hasFocus ? EMPTY : IDLE,
            hasFocus && isWideScreen() ? FOCUS_FIXED : EMPTY,
            hasFocus && !isWideScreen() ? FOCUS_STATIC : EMPTY, 
        ].filter(Boolean));
    }, [
        hasFocus,
        currentBreakpoint
    ]);
    const handleKeyUp = (event)=>{
        if (event.key === 'Enter') {
            handleFocus();
        }
    };
    const renderSearch = ()=>/*#__PURE__*/ React.createElement(InstantSearch, {
            searchClient: searchClient,
            indexName: productIndex.name
        }, /*#__PURE__*/ React.createElement(FocusTrap, {
            active: hasFocus
        }, hasFocus && /*#__PURE__*/ React.createElement(LockBodyScroll, null), /*#__PURE__*/ React.createElement(motion.div, {
            role: "dialog",
            "aria-modal": true,
            "aria-label": intl.getLabel('common.actions.search'),
            animate: pose,
            className: classnames('search-bar', {
                'search-bar--focus': hasFocus
            })
        }, /*#__PURE__*/ React.createElement(Configure, {
            hitsPerPage: 4,
            clickAnalytics: true,
            filters: productIndex.filters
        }), indexes.map((index)=>/*#__PURE__*/ React.createElement(FilteredIndex, {
                key: index.name,
                index: index,
                hasAnalytics: true,
                hitsPerPage: index.type === 'content' ? 8 : 4
            })
        ), /*#__PURE__*/ React.createElement(motion.div, {
            className: "search-bar__pose-container",
            variants: posedSearchBar,
            initial: "idle",
            animate: pose
        }, /*#__PURE__*/ React.createElement("div", {
            className: "search-bar__container"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "search-bar__box-container"
        }, /*#__PURE__*/ React.createElement(SearchBox, {
            hasFocus: hasFocus,
            handleFocus: handleFocus,
            handleClose: handleClose,
            handleSubmit: onSubmit,
            forceFocus: !!forceFocus
        })))), /*#__PURE__*/ React.createElement(motion.div, {
            variants: posedContent,
            initial: "idle",
            animate: pose,
            className: "search-bar__content"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "search-bar__content-container"
        }, hasFocus && /*#__PURE__*/ React.createElement(SearchResults, {
            pageRoutes: pageRoutes,
            productIndex: productIndex,
            storeIndex: resultConfig.storesIndex,
            suggestionIndex: resultConfig.suggestionsIndex,
            contentIndex: resultConfig.contentIndex,
            onSubmit: onSubmit
        })), /*#__PURE__*/ React.createElement("div", {
            className: "search-bar__content-scroll-indicator"
        })), /*#__PURE__*/ React.createElement(Backdrop, {
            className: "search-bar__backdrop",
            onClick: handleClose,
            isVisible: hasFocus,
            poseConfig: {
                exit: {
                    opacity: 0
                },
                enter: {
                    opacity: 1,
                    transition: {
                        delay: 0.25
                    }
                }
            }
        }))))
    ;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, !isMobile && /*#__PURE__*/ React.createElement("input", {
        className: "search-bar__input",
        onClick: handleFocus,
        onKeyDown: handleKeyUp,
        onInput: handleFocus,
        placeholder: intl.getLabel('search.searchField.placeholder'),
        "data-t": "search-bar",
        "aria-label": intl.getLabel('common.actions.search'),
        // We handle this in the opened search bar
        value: ""
    }), hasFocus || forceFocus ? renderSearch() : null);
};
var SearchBar$1 = /*#__PURE__*/ React.memo(SearchBar);

export { SearchBar, SearchBar$1 as default };
//# sourceMappingURL=SearchBar.js.map
